import React, { useCallback, useState } from 'react';
import { formatLongDate } from '../../utils/date';
import PartialSummaryNotice from '../PartialSummaryNotice';
import { ROLE_LABEL, type Shot } from '../report/PagesAssessed';
import ScreenshotLightbox from '../report/ScreenshotLightbox';
import CrosscheckBanner from '../report/CrosscheckBanner';
import { useReportScreenshots } from '../../hooks/useReportScreenshots';
import { isCleanReport, overallGrade, pickLeadSection, salesHeadline, salesRow } from '../../utils/salesReport';
import { T } from '../../designTokens';
import { formatPriceFromCents } from '../../utils/price';
import type { ComplianceCheck, SalesReportSummary, SectionName } from '../../utils/types';
import { fixBadge, groupIssues } from '../report/fixGroups';
import { LockIcon } from '../Icons';
import { CONSENT_CHECK, GPC_CHECK, type ConsumerVerdict } from '../../utils/audience';
import {
  CONSENT_HEADLINE, CONSENT_ROW, CONSUMER_FINE_PRINT, CONSUMER_SUB, GPC_LINE, GPC_ROW,
  OTHER_CHECKS_SUMMARY, UNLOCK_ITEMS, type ConsumerRow,
} from '../../utils/consumerCopy';

/** A capture plus the failing sections it shows the primary page of (#1474) */
type SalesShot = Shot & { evidenceFor?: SectionName[] };

/** Where a CTA renders: under the headline, or under "The full audit shows". */
export type TeaserCtaSlot = 'hero' | 'close';

export interface ReportTeaserProps {
  reportId: string;
  shareToken?: string | null;
  summary: SalesReportSummary;
  /**
   * The viewer owns the report (or holds its claim), so the audit already
   * ran: the close says "full report", not "full audit", and the free-audit
   * fine print is left out.
   */
  ownerView: boolean;
  /** The CTA slot, the only part that differs between viewers (#1807). */
  cta: (where: TeaserCtaSlot) => React.ReactNode;
  /** Rendered below the close CTA (the GPC-only note, "Email me this report"). */
  afterCta?: React.ReactNode;
  /**
   * Rendered right under the close CTA, before `afterCta`: the Privisy
   * Shield offer on a locked report (#2132), beside the unlock offer as the
   * other way to act on the findings rather than below everything else.
   */
  alternateOffer?: React.ReactNode;
  /** Passed to PartialSummaryNotice: a GPC-only scan surfaces errors only. */
  partialOnlyErrors?: boolean;
  /**
   * #2046: the visitor came through /check to see whether a site they visit
   * ignores their opt-out. Leads with that answer, drops the penalty line and
   * sells what a consumer gets. Absent: the default teaser, unchanged.
   */
  consumer?: ConsumerVerdict;
  /**
   * The owner's CCPA checks (`collectCcpaChecks` over the locked ScanResult).
   * The locked mask keeps each check's name, status and severity and strips
   * the proof, so the teaser names what failed and keeps the evidence and fix
   * locked. Read only in the owner view, never the consumer one.
   */
  findings?: ComplianceCheck[];
}

/**
 * The non-owner fine print: what the free audit gives away and what the
 * unlock costs (#2133). The price comes from the summary, never a constant
 * (#970); when it is missing the sentence names no price rather than guess.
 */
export function freeAuditFinePrint(domain: string, packPrice?: number): string {
  const price = packPrice !== undefined && Number.isFinite(packPrice) && packPrice > 0
    ? ` ${formatPriceFromCents(packPrice)}`
    : '';
  return `The free audit checks ${domain} the same way, with no account or card. The grade and section results are free. The findings and fixes unlock with a one-time${price} purchase.`;
}

/** Rows the findings list shows before its "+N more" line. */
const FINDINGS_SHOWN = 6;

/**
 * The owner's failing and review checks, worst first, in the full report's
 * order (Fix these, then Recommended). A Confirm warning is not a finding
 * (#1653), so it stays out. Names and severities only: the evidence and the
 * fix are what the unlock buys.
 */
function LockedFindings({ checks }: { checks: ComplianceCheck[] }) {
  const groups = groupIssues(checks);
  const listed = [
    ...groups.fix.map((check) => ({ check, badge: fixBadge(check, 'fix') })),
    ...groups.recommended.map((check) => ({ check, badge: fixBadge(check, 'recommended') })),
  ];
  if (listed.length === 0) return null;

  const issueCount = groups.fix.length;
  const warningCount = groups.recommended.length;
  const countLabel = [
    issueCount > 0 ? `${issueCount} issue${issueCount === 1 ? '' : 's'}` : null,
    warningCount > 0 ? `${warningCount} warning${warningCount === 1 ? '' : 's'}` : null,
  ].filter(Boolean).join(', ');
  const hidden = listed.length - FINDINGS_SHOWN;

  return (
    <section id="sales-findings" className="sales-findings" aria-labelledby="sales-findings-title">
      <div className="sales-checked__head">
        <h2 id="sales-findings-title" className="sales-h2">What we found</h2>
        <p className="sales-overall">{countLabel}</p>
      </div>
      <ul className="sales-rows">
        {listed.slice(0, FINDINGS_SHOWN).map(({ check, badge }) => (
          <li key={check.id} className="sales-row">
            <span className="sales-row__area">{check.name}</span>
            <span className="sales-row__result sales-locked">
              <span aria-hidden="true" className="sales-locked__icon"><LockIcon size={14} /></span>
              Evidence and fix locked
            </span>
            <span className={`severity-badge severity-badge--${badge.modifier} sales-findings__badge`}>{badge.text}</span>
          </li>
        ))}
      </ul>
      {hidden > 0 && <p className="sales-findings__more">+{hidden} more in the full report</p>}
    </section>
  );
}

/** "https://example.com/privacy" → "example.com/privacy", for captions. */
function bareUrl(url: string): string {
  return url.replace(/^https?:\/\//, '').replace(/\/$/, '');
}

/**
 * One capture at full width. Disabled while its blob loads (src undefined)
 * and with a visible "unavailable" state when the fetch failed (src null),
 * matching ScreenshotThumb, so a failure never reads as "still loading".
 */
function ShotPlate({ shot, src, caption, onOpen }: {
  shot: SalesShot;
  src?: string | null;
  caption: React.ReactNode;
  onOpen: (shot: Shot) => void;
}) {
  const label = ROLE_LABEL[shot.ref.role];
  const failed = src === null;
  const ratio = shot.ref.width > 0 && shot.ref.height > 0 ? `${shot.ref.width} / ${shot.ref.height}` : '16 / 9';
  return (
    <figure className="sales-shot">
      <button
        type="button"
        className="sales-shot__button"
        style={{ aspectRatio: ratio }}
        onClick={() => onOpen(shot)}
        disabled={!src}
        aria-label={failed ? `Screenshot unavailable: ${label}, ${shot.page.url}` : `Open screenshot: ${label}, ${shot.page.url}`}
      >
        {src ? <img src={src} alt={`${label}: ${shot.page.url}`} /> : null}
        {failed ? <span className="sales-shot__missing">Screenshot unavailable</span> : null}
      </button>
      <figcaption>{caption}</figcaption>
    </figure>
  );
}

/** A consumer-facing check row (#2046): a plain label over the catalog name. */
function ConsumerCheckRow({ row, checkName }: { row: ConsumerRow; checkName: string }) {
  return (
    <li className="sales-row">
      <span className="sales-row__area">{row.label}</span>
      <span className="sales-row__result sales-mono">{checkName}</span>
      <span className="sales-badge" data-tone={row.tone}>{row.badge}</span>
    </li>
  );
}

/**
 * The one locked-report teaser (#1807), shared by `#/sales/:id` and a locked
 * `#/scan/:id`. Built from the redacted summary only: grade, section results
 * and the homepage capture. Who is looking decides only the CTA slot.
 */
export default function ReportTeaser({
  reportId, shareToken, summary, ownerView, cta, afterCta, alternateOffer, partialOnlyErrors = false, consumer, findings,
}: ReportTeaserProps) {
  // The summary lists only refs the image route serves (a locked report: the
  // homepage capture alone), so the list is exactly what may be fetched.
  // Images load through fetchReportScreenshot → object URL: the CSP img-src
  // allows blob: but not the API origin (#1393).
  const shots: SalesShot[] = (summary.screenshots ?? []).map(({ key, role, pageUrl, width, height, evidenceFor }) => ({
    page: { url: pageUrl },
    ref: { key, role, width, height },
    evidenceFor,
  }));
  const lead = pickLeadSection(summary.sections);
  // The hero shows the prospect's own homepage; the close shows the page the
  // lead finding was read from (#1474), when that is a different capture.
  const heroShot = shots.find((s) => s.ref.role === 'homepage') ?? shots[0] ?? null;
  const evidenceShot = lead
    ? shots.find((s) => s.evidenceFor?.includes(lead.sectionKey) && s.ref.key !== heroShot?.ref.key) ?? null
    : null;
  const shownShots = [heroShot, evidenceShot].filter((s): s is SalesShot => s !== null);
  const sources = useReportScreenshots(shownShots.length > 0 ? reportId : null, shownShots.map((s) => s.ref.key), shareToken);
  const [openShot, setOpenShot] = useState<Shot | null>(null);
  const closeLightbox = useCallback(() => setOpenShot(null), []);
  const openLightboxSrc = openShot ? sources[openShot.ref.key] : undefined;

  const sectionRows = (
    <ul className="sales-rows">
      {summary.sections.map((section) => {
        const row = salesRow(section);
        return (
          <li key={section.sectionKey} className="sales-row">
            <span className="sales-row__area">{row.area}</span>
            <span className="sales-row__result">{row.result}</span>
            <span className="sales-badge" data-tone={row.tone}>{row.badge}</span>
          </li>
        );
      })}
    </ul>
  );

  const scanDate = new Date(summary.scanDate);
  const headline = salesHeadline(summary.domain, summary.sections);
  // The owner of a site with nothing failed or to review: there is nothing
  // to fix, so the close sells the record of what passed instead.
  const clean = ownerView && isCleanReport(summary.sections, findings);

  const evidenceCaption = evidenceShot && (
    <>
      {evidenceShot.ref.role === 'policy' ? 'The page we read as your privacy policy' : ROLE_LABEL[evidenceShot.ref.role]}
      <span className="sales-mono">{bareUrl(evidenceShot.page.url)}</span>
    </>
  );

  // Never sell a screenshot already on screen (#1393). A locked report shows
  // its homepage capture for free and keeps the rest behind the unlock.
  const evidenceCopy = shownShots.length === 0
    ? 'Every failing check shows what we found: the copy and network requests we captured for it, plus page screenshots taken during the scan.'
    : summary.locked
      ? 'Every failing check shows what we found: the copy and network requests we captured for it, plus screenshots of the other pages the scan visited.'
      : 'Every failing check shows what we found: the copy and network requests we captured for it.';

  return (
    <>
      <main className="sales-page">
        <PartialSummaryNotice sections={summary.sections} onlyErrors={partialOnlyErrors} />

        <section className={heroShot ? 'sales-hero' : 'sales-hero sales-hero--solo'}>
          <div className="sales-hero__copy">
            <p className="sales-meta">
              Prepared for <span className="sales-mono">{summary.domain}</span> on {formatLongDate(scanDate)}
            </p>
            {consumer ? (
              <>
                {/* Pass/fail only: the check details name the trackers, so
                    they are never read here (#2046). */}
                <h1 className="sales-headline">{CONSENT_HEADLINE[consumer.consent]}</h1>
                <p className="sales-sub">{GPC_LINE[consumer.gpc]} {CONSUMER_SUB}</p>
              </>
            ) : (
              <>
                <h1 className="sales-headline">
                  {headline.lead}<em data-tone={headline.tone}>{headline.emphasis}</em>{headline.tail}
                </h1>
                <p className="sales-sub">We loaded your site from the outside, the way a regulator or customer would, in a real browser.</p>
              </>
            )}
            <div className="sales-ctas">
              {cta('hero')}
              <button
                type="button"
                className="sales-link no-print"
                onClick={() => document.getElementById('sales-checked')?.scrollIntoView({ behavior: 'smooth' })}
              >
                See what we checked
              </button>
            </div>
          </div>
          {heroShot && (
            <ShotPlate
              shot={heroShot}
              src={sources[heroShot.ref.key]}
              onOpen={setOpenShot}
              caption={heroShot.ref.role === 'homepage'
                ? `${summary.domain}, as our browser loaded it`
                : <>{ROLE_LABEL[heroShot.ref.role]}<span className="sales-mono">{bareUrl(heroShot.page.url)}</span></>}
            />
          )}
        </section>

        {/* AI Crosscheck counts only (#1658): the redacted summary carries no
            quotes or check names. Absent when no verdict is displayed. */}
        <CrosscheckBanner counts={summary.crosscheck} variant="summary" />

        <section id="sales-checked" aria-labelledby="sales-checked-title">
          <div className="sales-checked__head">
            <h2 id="sales-checked-title" className="sales-h2">What we checked</h2>
            {/* The outreach email quotes the letter grade, so the page states it too. */}
            <p className="sales-overall">
              Overall grade <span className="sales-mono">{overallGrade(summary.overallScore, summary.letterGrade)}</span>
            </p>
          </div>
          {consumer ? (
            <>
              <ul className="sales-rows">
                <ConsumerCheckRow row={CONSENT_ROW[consumer.consent]} checkName={CONSENT_CHECK} />
                <ConsumerCheckRow row={GPC_ROW[consumer.gpc]} checkName={GPC_CHECK} />
              </ul>
              <details className="sales-more">
                <summary>{OTHER_CHECKS_SUMMARY}</summary>
                {sectionRows}
              </details>
            </>
          ) : sectionRows}
          {!consumer && summary.enforcementCaseCount > 0 && (
            <p className="sales-enforcement">
              <strong style={{ color: T.gray900 }}>{summary.totalEnforcementExposure}</strong> in penalties from {summary.enforcementCaseCount} related California AG enforcement action{summary.enforcementCaseCount > 1 ? 's' : ''}.
            </p>
          )}
        </section>

        {ownerView && !consumer && findings && <LockedFindings checks={findings} />}

        <section className={evidenceShot ? 'sales-close' : 'sales-close sales-close--solo'} aria-labelledby="sales-adds-title">
          {evidenceShot && (
            <ShotPlate shot={evidenceShot} src={sources[evidenceShot.ref.key]} onOpen={setOpenShot} caption={evidenceCaption} />
          )}
          <div className="sales-adds">
            <h2 id="sales-adds-title" className="sales-h2">{ownerView ? 'The full report shows' : 'The full audit shows'}</h2>
            {consumer ? (
              <ul>
                {UNLOCK_ITEMS.map((item) => (
                  <li key={item.title}>
                    <strong>{item.title}</strong>
                    {item.body}
                  </li>
                ))}
              </ul>
            ) : clean ? (
              <ul>
                <li>
                  <strong>Every check it passed</strong>
                  Each check that ran, by name, so you have a record of what was verified on {summary.domain}.
                </li>
                <li>
                  <strong>Tracker and cookie inventory</strong>
                  Every third-party vendor that loaded, grouped by compliance risk.
                </li>
              </ul>
            ) : (
              <ul>
                <li>
                  <strong>Findings with evidence</strong>
                  {evidenceCopy}
                </li>
                <li>
                  <strong>Fixes in priority order</strong>
                  Specific fixes for each issue, ranked by enforcement risk and written for whoever edits the site.
                </li>
                <li>
                  <strong>Tracker and cookie inventory</strong>
                  Every third-party vendor that loaded, grouped by compliance risk.
                </li>
              </ul>
            )}
            <div className="sales-ctas">{cta('close')}</div>
            {!ownerView && consumer && <p className="sales-fine">{CONSUMER_FINE_PRINT}</p>}
            {!ownerView && !consumer && (
              <p className="sales-fine">{freeAuditFinePrint(summary.domain, summary.packPrice)}</p>
            )}
            {alternateOffer}
            {afterCta}
          </div>
        </section>

        <footer className="sales-foot">
          Privisy checks public pages only. This summary is not legal advice.
          <span className="sales-print-only"> Scanner {summary.scannerVersion}, scoring {summary.scoringVersion}.</span>
        </footer>
      </main>

      {openShot && openLightboxSrc && (
        <ScreenshotLightbox
          src={openLightboxSrc}
          url={openShot.page.url}
          roleLabel={ROLE_LABEL[openShot.ref.role]}
          width={openShot.ref.width}
          height={openShot.ref.height}
          onClose={closeLightbox}
        />
      )}
    </>
  );
}
